import { useMemo } from 'react';
import {
  useGetOrganization,
  useGetChildOrganizations,
  PermissionAction,
  PermissionResource,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { PermissionBoundary, useCan, useUserSetupStore } from '@sib/auth';
import { Button, Skeleton, TOPBAR_HEIGHT } from '@sib/ui';
import { MenuIcon } from '@spendbrain/common/svg/MenuIcon';
import { OrganizationSwitch } from '../OrganizationSwitch';
import { OrganizationSwitchButton } from '../OrganizationSwitchButton';
import { OrganizationLogo } from './OrganizationLogo';
import { ShareButton } from './ShareButton';
import { UploadFileButton } from './UploadFileButton';
import SibLogo from '../../../../public/images/sib.svg';

export interface TopBarProps {
  onNavOpen: () => void;
  onUploadClick: () => void;
}

export const TopBar = ({ onNavOpen, onUploadClick }: TopBarProps) => {
  const {
    setParentOrganization,
    setOrganization,
    entryPointOrganization,
    setEntryPointOrganization,
    organization: { id: organizationId },
  } = useUserSetupStore();
  const can = useCan();
  const {
    data: organization,
    isFetching,
    isLoading,
  } = useGetOrganization(organizationId);

  const rootParentId =
    organization?.parentOrganization?.id || organization?.id || '';

  const shouldFetchChildOrganizations =
    entryPointOrganization || !organization?.parentOrganization;

  const { data: rootChildOrgsData } = useGetChildOrganizations(
    shouldFetchChildOrganizations ? rootParentId : '',
    {
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
    }
  );

  const rootChildOrganizations = useMemo(
    () => rootChildOrgsData?.items ?? [],
    [rootChildOrgsData]
  );

  const handleSetEntryPointIfNeeded = () => {
    if (!entryPointOrganization && organization) {
      setEntryPointOrganization(organization);
    }
  };

  return (
    <header
      className="fixed top-0 right-0 left-0 z-50 flex w-[100vw] items-center border-b bg-white pr-6 pl-[12px] shadow-md"
      style={{ height: TOPBAR_HEIGHT }}
    >
      <div className="mr-2 flex items-center justify-end">
        <Button
          variant="ghost"
          size="custom"
          className="text-primary mr-1 size-8 sm:mr-8"
          onClick={onNavOpen}
        >
          <MenuIcon />
        </Button>
        {isFetching || isLoading ? (
          <Skeleton className="h-[32px] w-[120px]" />
        ) : (entryPointOrganization && organization) ||
          (organization &&
            !organization.parentOrganization &&
            rootChildOrganizations.length > 0) ? (
          <OrganizationSwitchButton
            organization={organization}
            organizationId={organizationId}
            setOrganizationId={(id: string, name: string) => {
              handleSetEntryPointIfNeeded();
              setOrganization({
                id,
                name,
              });
            }}
            setParentOrganization={setParentOrganization}
            entryPointOrganization={entryPointOrganization || organization}
            setEntryPointOrganization={setEntryPointOrganization}
          />
        ) : (
          <OrganizationLogo organization={organization} />
        )}
      </div>
      <div className="ml-auto flex items-center gap-4">
        {/* Hide the NotificationBell */}
        {/* <Button variant="ghost" size="icon" className="size-8">
            <NotificationBell showDot={true} />
          </Button> */}
        <OrganizationSwitch className="xs:text-base xs:p-4 p-2 text-sm" />
        {can({
          permissions: {
            actions: PermissionAction.CREATE,
            resources: PermissionResource.Documents,
          },
        }) && <UploadFileButton onClick={onUploadClick} />}
        <PermissionBoundary
          permissionsRequired={[
            {
              resources: PermissionResource.AccessShare,
              actions: PermissionAction.READ,
            },
            {
              resources: PermissionResource.AccessShare,
              actions: PermissionAction.CREATE,
            },
          ]}
          operator="OR"
        >
          <ShareButton className="xs:text-base xs:p-4 p-2.5 text-xs" />
        </PermissionBoundary>
        <div>
          <img src={SibLogo} alt="Logo" width={100} height={60} />
        </div>
      </div>
    </header>
  );
};
